import ExcelJS from "exceljs";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

// Columns follow the RFQ quotation sheet: customer's MPN/make/spec/volume on the
// left, then what we quote (MPN, make, unit price, SPQ, MOQ, availability, lead
// time). A line total is added so the sheet carries the quotation value.
const TBA = "TBA";

const slug = (s) =>
  String(s || "")
    .replace(/\.[^/.]+$/, "")
    .replace(/[^\w-]+/g, "-")
    .replace(/^-+|-+$/g, "");

export const quotationFileName = (meta, ext) => {
  const base = slug(meta.bomFileName);
  return `Quotation${base ? `_${base}` : ""}.${ext}`;
};

const metaRows = (meta, data) => [
  ["Quotation No.", meta.quoteNumber || "-"],
  ["Date", meta.date],
  ["Valid until", meta.validUntil || "-"],
  ["Customer", meta.customer || "-"],
  ["Currency", meta.currency],
  ["Items quoted", `${data.quotedCount} of ${data.totalCount}`],
];

const lineCells = (l) =>
  l.quoted
    ? [
        l.slNo,
        l.mpn,
        l.manufacturer,
        l.description,
        l.qty,
        l.quotedMpn,
        l.quotedMake,
        l.unitPrice,
        l.lineTotal,
        l.spq,
        l.moq,
        l.availability,
        l.leadTime,
        l.status,
      ]
    : [l.slNo, l.mpn, l.manufacturer, l.description, l.qty, TBA, "", "", "", "", "", "", "", l.status];

const HEADERS = [
  "Sl No.",
  "MPN",
  "Manufacturer",
  "Technical Specifications",
  "Volume",
  "Quoted MPN",
  "Quoted Make",
  "Unit price",
  "Total",
  "SPQ",
  "MOQ",
  "Availability",
  "Lead time",
  "Status",
];

export const buildQuotationXlsxBlob = async (meta, data) => {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("Quotation");
  const cur = meta.currency;

  ws.addRow(["QUOTATION"]).font = { bold: true, size: 14 };
  ws.addRow([]);
  for (const [k, v] of metaRows(meta, data)) {
    const r = ws.addRow([k, v]);
    r.getCell(1).font = { bold: true };
  }
  ws.addRow([]);

  const headerRow = ws.addRow(HEADERS.map((h) => (h.startsWith("Unit price") || h === "Total" ? `${h} (${cur})` : h)));
  headerRow.eachCell((c) => {
    c.font = { bold: true, size: 10 };
    c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD9E1F2" } };
    c.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
    c.border = { bottom: { style: "thin" } };
  });
  const headerRowNumber = headerRow.number;

  for (const l of data.lines) {
    const r = ws.addRow(lineCells(l));
    r.font = { size: 10 };
    if (!l.quoted) r.font = { size: 10, color: { argb: "FF9A9A9A" } };
  }
  const firstData = headerRowNumber + 1;
  const lastData = headerRowNumber + data.lines.length;

  const totalRow = ws.addRow([]);
  totalRow.getCell(8).value = "Total";
  totalRow.getCell(9).value = {
    formula: `SUM(I${firstData}:I${lastData})`,
    result: data.total,
  };
  totalRow.font = { bold: true };

  ws.addRow([]);
  if (meta.notes) {
    ws.addRow(["Notes"]).font = { bold: true };
    for (const line of String(meta.notes).split("\n")) ws.addRow([line]);
  }

  const widths = [7, 22, 20, 52, 10, 22, 20, 14, 14, 9, 9, 13, 12, 13];
  widths.forEach((w, i) => (ws.getColumn(i + 1).width = w));
  for (const col of [8, 9]) ws.getColumn(col).numFmt = "#,##0.00##";
  ws.getColumn(8).numFmt = "#,##0.00####";
  for (const col of [5, 10, 11, 12]) ws.getColumn(col).numFmt = "#,##0";
  ws.views = [{ state: "frozen", ySplit: headerRowNumber }];

  // The info block shares columns with the table; keep its numeric cells left.
  for (let r = 3; r < headerRowNumber; r++) {
    ws.getCell(r, 2).alignment = { horizontal: "left" };
    ws.getCell(r, 2).numFmt = "@";
  }

  const buffer = await wb.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};

// Internal costing worksheet (steps 7–15), laid out like the team's Excel
// sheet. Derived columns are live formulas off the FX cell, so the sheet can be
// audited and tweaked in Excel; cached results let it open with values.
const WS_HEADERS = [
  "Sl No.", "Part Number", "Quoted MPN", "Req Qty", "HS Code", "Unit Price",
  "Distributor", "INR", "Duty %", "Duty Value", "Freight / unit", "Landing Value",
  "Margin %", "Margin Value", "Resale Value", "Line Total", "Status",
];

export const worksheetFileName = (meta) =>
  quotationFileName(meta, "xlsx").replace(/^Quotation/, "Worksheet");

export const buildWorksheetXlsxBlob = async (meta, worksheet) => {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("Worksheet");

  ws.addRow(["QUOTATION WORKSHEET (internal)"]).font = { bold: true, size: 14 };
  ws.addRow(["Quotation No.", meta.quoteNumber || "-"]);
  ws.addRow(["Customer", meta.customer || "-"]);
  ws.addRow([`FX (1 ${worksheet.sourceCurrency || "USD"} = INR)`, Number(meta.fxRate) || 0]);
  ws.addRow(["Total freight (INR)", Number(meta.totalFreight) || 0]);
  for (let r = 2; r <= 5; r++) ws.getCell(r, 1).font = { bold: true };
  const fxCell = "$B$4";
  wb.definedNames.add("Worksheet!$B$4", "FX");
  ws.addRow([]);

  const headerRow = ws.addRow(WS_HEADERS);
  headerRow.eachCell((c) => {
    c.font = { bold: true, size: 10 };
    c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD9E1F2" } };
    c.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
    c.border = { bottom: { style: "thin" } };
  });
  const first = headerRow.number + 1;

  worksheet.lines.forEach((l, i) => {
    const r = first + i;
    if (!l.quoted) {
      const row = ws.addRow([l.slNo, l.mpn, "", l.qty, "", "", "", "", "", "", "", "", "", "", "", "", "not found"]);
      row.font = { size: 10, color: { argb: "FF9A9A9A" } };
      return;
    }
    const f = (formula, result) => ({ formula, result });
    const row = ws.addRow([
      l.slNo,
      l.mpn,
      l.quotedMpn,
      l.qty,
      l.hsnCode || "",
      l.buyUnit,
      l.distributor,
      f(`F${r}*${fxCell}`, l.inrUnit),
      l.dutyPct / 100,
      f(`H${r}*I${r}`, l.dutyUnit),
      l.freightUnit,
      f(`H${r}+J${r}+K${r}`, l.landingUnit),
      l.marginPct / 100,
      f(`L${r}*M${r}`, l.marginUnit),
      f(`L${r}+N${r}`, l.resellUnit),
      f(`O${r}*D${r}`, l.resellUnit * l.qty),
      l.status,
    ]);
    row.font = { size: 10 };
  });
  const last = first + worksheet.lines.length - 1;

  const totalRow = ws.addRow([]);
  totalRow.getCell(7).value = "Total";
  totalRow.getCell(16).value = { formula: `SUM(P${first}:P${last})`, result: worksheet.totals.resell };
  totalRow.font = { bold: true };

  const widths = [7, 22, 22, 10, 12, 12, 14, 12, 8, 12, 12, 13, 9, 12, 13, 15, 10];
  widths.forEach((w, i) => (ws.getColumn(i + 1).width = w));
  for (const col of [6, 8, 10, 11, 12, 14, 15]) ws.getColumn(col).numFmt = "#,##0.00####";
  for (const col of [9, 13]) ws.getColumn(col).numFmt = "0.00%";
  ws.getColumn(16).numFmt = "#,##0.00";
  ws.getColumn(4).numFmt = "#,##0";
  ws.getCell("B4").numFmt = "0.00##";
  ws.getCell("B5").numFmt = "#,##0.00";
  ws.views = [{ state: "frozen", ySplit: headerRow.number, xSplit: 2 }];
  ws.autoFilter = { from: { row: headerRow.number, column: 1 }, to: { row: last, column: WS_HEADERS.length } };

  const buffer = await wb.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};

const fmt = (n, dp = 2) =>
  Number(n).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: dp,
  });

export const buildQuotationPdfBlob = (meta, data) => {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 32;
  const cur = meta.currency;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("QUOTATION", margin, 44);

  doc.setFontSize(9);
  const info = metaRows(meta, data);
  const half = Math.ceil(info.length / 2);
  [info.slice(0, half), info.slice(half)].forEach((col, ci) => {
    col.forEach(([k, v], i) => {
      const x = margin + ci * 260;
      const y = 66 + i * 13;
      doc.setFont("helvetica", "bold");
      doc.text(`${k}:`, x, y);
      doc.setFont("helvetica", "normal");
      doc.text(String(v), x + 75, y);
    });
  });

  const body = data.lines.map((l) =>
    l.quoted
      ? [
          l.slNo,
          l.mpn,
          l.description,
          l.qty.toLocaleString("en-US"),
          l.quotedMpn,
          l.quotedMake,
          fmt(l.unitPrice, 4),
          fmt(l.lineTotal),
          l.spq.toLocaleString("en-US"),
          l.moq.toLocaleString("en-US"),
          l.availability.toLocaleString("en-US"),
          l.leadTime,
          l.status,
        ]
      : [l.slNo, l.mpn, l.description, l.qty.toLocaleString("en-US"), TBA, "", "", "", "", "", "", "", l.status],
  );

  autoTable(doc, {
    startY: 66 + half * 13 + 12,
    margin: { left: margin, right: margin, bottom: 40 },
    head: [
      [
        "Sl",
        "MPN",
        "Description",
        "Volume",
        "Quoted MPN",
        "Quoted Make",
        `Unit price (${cur})`,
        `Total (${cur})`,
        "SPQ",
        "MOQ",
        "Avail.",
        "Lead time",
        "Status",
      ],
    ],
    body,
    foot: [
      [
        { content: "Total", colSpan: 7, styles: { halign: "right" } },
        { content: fmt(data.total), styles: { halign: "right" } },
        { content: "", colSpan: 5 },
      ],
    ],
    showFoot: "lastPage",
    styles: { fontSize: 7, cellPadding: 3, overflow: "linebreak" },
    headStyles: { fillColor: [95, 71, 201], textColor: 255, fontSize: 7.5 },
    footStyles: { fillColor: [240, 238, 251], textColor: 20, fontStyle: "bold" },
    columnStyles: {
      0: { cellWidth: 22 },
      1: { cellWidth: 92 },
      2: { cellWidth: "auto" },
      3: { halign: "right", cellWidth: 40 },
      4: { cellWidth: 88 },
      5: { cellWidth: 62 },
      6: { halign: "right", cellWidth: 52 },
      7: { halign: "right", cellWidth: 56 },
      8: { halign: "right", cellWidth: 32 },
      9: { halign: "right", cellWidth: 36 },
      10: { halign: "right", cellWidth: 40 },
      11: { cellWidth: 44 },
      12: { cellWidth: 50 },
    },
  });

  if (meta.notes) {
    let y = doc.lastAutoTable.finalY + 18;
    const pageH = doc.internal.pageSize.getHeight();
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    if (y > pageH - 60) {
      doc.addPage();
      y = 44;
    }
    doc.text("Notes", margin, y);
    doc.setFont("helvetica", "normal");
    const lines = doc.splitTextToSize(String(meta.notes), pageW - margin * 2);
    doc.text(lines, margin, y + 13);
  }

  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text(
      `Page ${i} of ${pages}`,
      pageW - margin,
      doc.internal.pageSize.getHeight() - 18,
      { align: "right" },
    );
  }
  return doc.output("blob");
};

export const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
